import config from 'constants/config'
import { ITEM_STATUS_CANCELLED, ITEM_STATUS_COMPLETED } from 'constants/cart'
import { CHECKOUT_ITEM_TYPE_CRUISE, CHECKOUT_ITEM_TYPE_TOUR_V2 } from 'constants/checkout'
import { PASSPORT_CARD_MAX_ADULTS } from 'constants/insurance'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { getInsuranceDatesFromOrder } from 'lib/order/orderInsuranceUtils'
import { isPassportCardStartDateWithinAllowedRange } from 'checkout/Components/CheckoutSteps/Review/PassportCardInsurance/passportCardUtils'

export interface CanAddPassportCardInsuranceToOrderOptions {
  regionCode: string
  isSpoofed: boolean
  /** cro_passport_card_tours_cruises_enabled - the same Optimizely rollout flag the checkout gate reads. */
  isToursCruisesFeatureEnabled: boolean
  /** Resolved with getInsuranceDestinationCountriesFromOrder; svc-insurance rejects an empty list. */
  destinationCountries: Array<string>
}

interface OrderItemWithStatus {
  status: string
}

function isActiveItem(item: OrderItemWithStatus): boolean {
  return item.status !== ITEM_STATUS_CANCELLED
}

function isCompletedItem(item: OrderItemWithStatus): boolean {
  return item.status === ITEM_STATUS_COMPLETED
}

function isPassportCardEnabledVertical(itemType: string): boolean {
  return isEnabledForFeature(config.PASSPORT_CARD_ENABLED_ITEM_TYPES, itemType)
}

function getCompletedTourItems(order: App.Order): Array<App.Tours.TourV2OrderItem> {
  return order.tourItems.filter(isCompletedItem)
}

function getCompletedCruiseItems(order: App.Order): Array<App.OrderCruiseItem> {
  return order.cruiseItems.filter(isCompletedItem)
}

// Every vertical collection on the order other than tours and cruises. Insurance,
// booking protection, gift cards, LuxPlus subscriptions, addons and tour optional
// experiences are add-ons of the booking rather than a vertical of their own.
function hasActiveItemsOutsideToursCruises(order: App.Order): boolean {
  const otherVerticalItems: Array<OrderItemWithStatus> = [
    ...order.items,
    ...order.bedbankItems,
    ...order.flightItems,
    ...order.experienceItems,
    ...order.carHireItems,
    ...order.transferItems,
    ...order.customOfferItems,
    ...order.merchandiseItems,
    ...order.merchandiseShippingItems,
  ]
  return otherVerticalItems.some(isActiveItem)
}

/**
 * Order-side twin of isStandaloneToursCruisesCart: the completed items are tours and/or
 * cruises from the PassportCard-enabled verticals (PASSPORT_CARD_ENABLED_ITEM_TYPES) and
 * nothing else. A tour and a cruise together cannot be priced by PassportCard, so such
 * orders are not standalone either.
 */
export function isStandaloneToursCruisesOrder(order: App.Order): boolean {
  const completedTours = getCompletedTourItems(order)
  const completedCruises = getCompletedCruiseItems(order)
  const enabledPrimaryItemCount =
    (isPassportCardEnabledVertical(CHECKOUT_ITEM_TYPE_TOUR_V2) ? completedTours.length : 0) +
    (isPassportCardEnabledVertical(CHECKOUT_ITEM_TYPE_CRUISE) ? completedCruises.length : 0)

  if (!enabledPrimaryItemCount) return false
  if (completedTours.length && completedCruises.length) return false

  return !hasActiveItemsOutsideToursCruises(order)
}

// The quote occupancy folds teenagers into adults (normaliseOccupancyForInsurance via
// getOccupantsFromExistingOrder). Types outside its vocabulary (e.g. 'traveller') never
// reach the quote, so they do not count towards the policy either.
function isQuotedAsAdult(travellerType: string): boolean {
  return travellerType === 'adult' || travellerType === 'teenager'
}

/**
 * Travellers the quote counts as adults across the completed tour and cruise items -
 * the same people getOccupantsFromExistingOrder puts on the quote, so the cap is
 * judged on the policy that would actually be issued.
 */
export function getToursCruisesAdultCountFromOrder(order: App.Order): number {
  const tourAdults = getCompletedTourItems(order)
    .flatMap(item => item.tour.travellers)
    .filter(traveller => isQuotedAsAdult(traveller.type)).length
  const cruiseAdults = getCompletedCruiseItems(order)
    .flatMap(item => item.passengers)
    .filter(passenger => isQuotedAsAdult(passenger.type)).length

  return tourAdults + cruiseAdults
}

/**
 * Whether the order holds anything PassportCard can quote as a trip: LE accommodation
 * with a reservation, bedbank stays, flights, tours or cruises. Experiences and other
 * add-on verticals have no PassportCard trip representation.
 */
export function hasPassportCardQuotableOrderItems(order: App.Order): boolean {
  return order.items.some(item => isActiveItem(item) && !!item.reservation) ||
    order.bedbankItems.some(isActiveItem) ||
    order.flightItems.some(isActiveItem) ||
    getCompletedTourItems(order).length > 0 ||
    getCompletedCruiseItems(order).length > 0
}

/**
 * The order narrowed to the items a PassportCard policy covers, applying the same status
 * rules as the trip mapper: active accommodation holding a reservation, active bedbank,
 * flight and experience items, completed tours and cruises. Destination countries derived
 * from this slice describe the protected trip - a cancelled item can neither pick the
 * vertical branch inside getInsuranceDestinationCountriesFromOrder nor contribute its
 * destination.
 */
export function getPassportCardInsurableOrderSlice(order: App.Order): App.Order {
  return {
    ...order,
    items: order.items.filter(item => isActiveItem(item) && !!item.reservation),
    bedbankItems: order.bedbankItems.filter(isActiveItem),
    tourItems: getCompletedTourItems(order),
    cruiseItems: getCompletedCruiseItems(order),
    flightItems: order.flightItems.filter(isActiveItem),
    experienceItems: order.experienceItems.filter(isActiveItem),
  }
}

/**
 * Post-purchase PassportCard eligibility for an existing order. Shared by the My Escapes
 * upsell and the checkout section gate so both make the same product decision.
 *
 * Spoof mode offers PassportCard on every quotable vertical; the public flow mirrors the
 * checkout gate for standalone tours/cruises (enabled region, adult cap, and the
 * cro_passport_card_tours_cruises_enabled Optimizely flag that controls the rollout).
 */
export function canAddPassportCardInsuranceToOrder(
  order: App.Order,
  options: CanAddPassportCardInsuranceToOrderOptions,
): boolean {
  if (order.insuranceItems.some(isActiveItem)) return false
  if (order.reserveForZeroDetails?.isActive) return false
  // Mirrors orderCanAddInsurance: bundle-and-save orders do not support insurance, and a
  // brand can turn post-purchase insurance off entirely.
  if (!config.ADD_INSURANCE_AFTER_PURCHASE) return false
  if (order.items.some(item => item.offer.bundleOfferId)) return false
  if (!isEnabledForFeature(config.PASSPORT_CARD_ENABLED_REGIONS, options.regionCode)) return false

  const dates = getInsuranceDatesFromOrder(order)
  if (!dates || !isPassportCardStartDateWithinAllowedRange(dates.startDate)) return false
  if (!options.destinationCountries.length) return false

  if (options.isSpoofed) return hasPassportCardQuotableOrderItems(order)

  if (!options.isToursCruisesFeatureEnabled) return false
  if (!isStandaloneToursCruisesOrder(order)) return false

  // A policy needs at least one quotable adult and PassportCard caps them at two.
  const quotedAdultCount = getToursCruisesAdultCountFromOrder(order)
  return quotedAdultCount >= 1 && quotedAdultCount <= PASSPORT_CARD_MAX_ADULTS
}
